import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ResendTicketsEmailResponseDto } from "@ac-expo/api-types";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { ticketingSalesQueryKeys } from "../query-keys";
import { handleApiCall } from "../api-error-handler";

const generateInvoices = async (orderId: string) => {
  try {
    const { data: response } = await privateAxios.post(
      `/orders/${orderId}/generate-invoices`
    );
    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGenerateInvoicesMutation = (eventId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateInvoices,
    onSuccess() {
      if (eventId)
        queryClient.invalidateQueries({
          queryKey: ticketingSalesQueryKeys.event(eventId),
        });
    },
  });
};

type CreateAccessLinksResult = {
  viewTicketsUrl: string;
  viewBadgesUrl: string;
};

type CreateOrderAccessLinksResult = CreateAccessLinksResult;

const createOrderAccessLinks = (orderId: string) =>
  handleApiCall(() =>
    privateAxios.post<CreateOrderAccessLinksResult>(
      `/guest-checkout/orders/${orderId}/access-links`
    )
  );

export const useCreateOrderAccessLinksMutation = () =>
  useMutation({ mutationFn: createOrderAccessLinks });

const createInvitationAccessLinks = (invitationId: string) =>
  handleApiCall(() =>
    privateAxios.post<CreateAccessLinksResult>(
      `/invitations/${invitationId}/access-links`
    )
  );

export const useCreateInvitationAccessLinksMutation = () =>
  useMutation({ mutationFn: createInvitationAccessLinks });

const resendOrderTicketsEmail = (orderId: string) =>
  handleApiCall(() =>
    privateAxios.post<ResendTicketsEmailResponseDto>(
      `/orders/${orderId}/resend-tickets-email`
    )
  );

export const useResendOrderTicketsEmailMutation = () =>
  useMutation({ mutationFn: resendOrderTicketsEmail });

const resendEventTicketsEmail = (eventId: string) =>
  handleApiCall(() =>
    privateAxios.post<ResendTicketsEmailResponseDto>(
      `/orders/events/${eventId}/resend-tickets-email`
    )
  );

export const useResendEventTicketsEmailMutation = () =>
  useMutation({ mutationFn: resendEventTicketsEmail });
